/**
 * @fileoverview TaskGrid component for displaying and managing tasks in a grid layout.
 * Provides functionality for viewing, editing, and managing task items.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTaskLogic } from './hooks/useTaskLogic';
import TaskModal from './TaskModal';
import './styles/TaskGrid.css';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
// Import Material UI icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import PauseIcon from '@mui/icons-material/Pause';

/**
 * TaskGrid component for displaying tasks in a grid layout.
 * Provides functionality for task management including adding, editing, and deleting tasks.
 * 
 * @component
 * @returns {React.ReactElement} The rendered TaskGrid component
 */
const TaskGrid = () => {
  const { 
    tasks,
    loading,
    activeTab,
    handleAddTask,
    handleEditTask,
    handleDeleteTask,
    handleTabChange,
    showTaskModal,
    setShowTaskModal,
    currentTask,
    handleSubmitTask,
    updateTaskStatus,
    editMode,
    setEditMode
  } = useTaskLogic();

  const { getAccessToken } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'status', order: 'asc' });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'grouped'
  const [groupedViewFilter, setGroupedViewFilter] = useState('regular'); // 'regular' or 'talent'
  const [expandedGroups, setExpandedGroups] = useState({}); // Track which groups are expanded
  const [clientScores, setClientScores] = useState({}); // Store client scores by client_id

  /**
   * Fetches client scores for all clients assigned to tasks.
   * Updates clientScores state with score data keyed by client_id.
   */
  useEffect(() => {
    const fetchClientScores = async () => {
      try {
        const token = await getAccessToken();
        const response = await axios.get(`${API_ENDPOINTS.LEADS.RANKS}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          console.log('Raw ranks response:', response.data);
          
          // Transform the array into an object keyed by both id and client_id
          const ranks = {};
          response.data.forEach(item => {
            if (item.id) {
              ranks[item.id] = item.score;
              if (item.client_id) {
                ranks[item.client_id] = item.score;
              }
            }
          });
          
          console.log('Transformed client scores:', ranks);
          setClientScores(ranks);
        }
      } catch (error) {
        console.error('Error fetching client scores:', error);
      }
    };

    if (tasks.length > 0) {
      fetchClientScores();
    }
  }, [tasks, getAccessToken]);

  /**
   * Handles closing the task modal.
   * 
   * @function handleCloseModal
   */
  const handleCloseModal = useCallback(() => {
    setShowTaskModal(false);
    setEditMode(false);
  }, [setShowTaskModal, setEditMode]);

  /**
   * Handles deleting a task.
   * 
   * @async
   * @function handleDelete
   * @param {string} taskId - ID of the task to delete
   * @returns {Promise<void>}
   */
  const handleDelete = useCallback(async (taskId) => {
    await handleDeleteTask(taskId);
  }, [handleDeleteTask]);

  /**
   * Handles editing a task.
   * 
   * @function handleEdit
   * @param {Object} task - Task to edit
   */
  const handleEdit = (task) => {
    console.log('Edit clicked, task:', task);
    handleEditTask(task);
    setShowTaskModal(true);
  };

  /**
   * Handles adding a new task.
   * 
   * @function handleAdd
   */
  const handleAdd = () => {
    handleAddTask();
    setShowTaskModal(true);
  };

  /**
   * Handles marking a task as complete.
   * 
   * @async
   * @function handleMarkComplete
   * @param {Object} task - Task to mark as complete
   * @returns {Promise<void>}
   */
  const handleMarkComplete = async (task) => {
    try {
      await updateTaskStatus(task._id, 'complete');
    } catch (error) {
      console.error('Error marking task complete:', error);
    }
  };

  /**
   * Handles marking a task as on hold.
   * 
   * @async
   * @function handleMarkHold
   * @param {Object} task - Task to mark as on hold
   * @returns {Promise<void>}
   */
  const handleMarkHold = async (task) => {
    try {
      await updateTaskStatus(task._id, 'hold');
    } catch (error) {
      console.error('Error marking task on hold:', error);
    }
  };

  /**
   * Formats a date string to American month/day format.
   * 
   * @function formatDate
   * @param {string} dateString - Date string in YYYY-MM-DD format
   * @returns {string} Formatted date as MM/DD
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    return `${month}/${day}`;
  };
  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <CheckCircleIcon className="status-icon completed" />;
      case 'in_progress':
        return <PlayCircleOutlineIcon className="status-icon in-progress" />;
      case 'hold':
        return <RadioButtonUncheckedIcon className="status-icon hold" />;
      default:
        return <RadioButtonUncheckedIcon className="status-icon active" />;
    }
  };

  /**
   * Gets the appropriate status icon based on task status.
   * 
   * @function getStatusIcon
   * @param {string} status - Task status
   * @returns {React.ReactElement} Status icon component
   */
  const getStatusText = (status) => {
    switch (status) {
      case 'complete':
        return 'COMPLETE';
      case 'in_progress':
        return 'IN PROGRESS';
      case 'hold':
        return 'HOLD';
      default:
        return 'ACTIVE';
    }
  };

  /**
   * Filters tasks based on search query.
   * Searches across title, description, assignees, and priority.
   * 
   * @function filteredTasks
   * @returns {Array} Filtered tasks
   */
  const filteredTasks = tasks.filter(task => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    
    // Search in title
    if (task.title?.toLowerCase().includes(query)) return true;
    
    // Search in description
    if (task.description?.toLowerCase().includes(query)) return true;
    
    // Search in priority
    if (task.priority?.toLowerCase().includes(query)) return true;
    
    // Search in assignees
    if (task.assignees?.some(assignee => 
      assignee.name?.toLowerCase().includes(query)
    )) return true;
    
    // Search in status
    if (getStatusText(task.status)?.toLowerCase().includes(query)) return true;
    
    return false;
  });

  /**
   * Sorts tasks by the configured field and order.
   * 
   * @function sortedTasks
   * @returns {Array} Sorted tasks
   */
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const { field, order } = sortConfig;
    
    let compareResult = 0;
    
    switch (field) {
      case 'status':
        const statusOrder = { 'in_progress': 0, 'active': 1, 'hold': 2, 'complete': 3 };
        const orderA = statusOrder[a.status] ?? 1;
        const orderB = statusOrder[b.status] ?? 1;
        compareResult = orderA - orderB;
        break;
        
      case 'title':
        compareResult = (a.title || '').localeCompare(b.title || '');
        break;
        
      case 'date':
        const dateA = a.due_date ? new Date(a.due_date).getTime() : 0;
        const dateB = b.due_date ? new Date(b.due_date).getTime() : 0;
        compareResult = dateA - dateB;
        break;
        
      case 'priority':
        const priorityOrder = { 'high': 0, 'medium': 1, 'low': 2 };
        const priorityA = priorityOrder[a.priority] ?? 1;
        const priorityB = priorityOrder[b.priority] ?? 1;
        compareResult = priorityA - priorityB;
        break;
        
      default:
        compareResult = 0;
    }
    
    return order === 'asc' ? compareResult : -compareResult;
  });

  /**
   * Handles sorting by a specific field.
   * 
   * @function handleSort
   * @param {string} field - Field to sort by
   */
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  /**
   * Groups tasks by creator/subject.
   * For Regular view: groups by employees
   * For Talent view: groups by clients (talents)
   * 
   * @function getGroupedTasks
   * @returns {Object} Object with creator names as keys and task arrays as values
   */
  const getGroupedTasks = () => {
    const grouped = {};
    
    sortedTasks.forEach(task => {
      // Determine the grouping key based on the current filter
      if (groupedViewFilter === 'regular') {
        // Group by employees
        const employees = task.assignees.filter(a => a.type === 'employee');
        if (employees.length > 0) {
          employees.forEach(emp => {
            if (!grouped[emp.name]) {
              grouped[emp.name] = [];
            }
            grouped[emp.name].push(task);
          });
        } else {
          // Tasks with no employee assignees
          if (!grouped['Unassigned']) {
            grouped['Unassigned'] = [];
          }
          grouped['Unassigned'].push(task);
        }
      } else {
        // Group by clients (talents)
        const clients = task.assignees.filter(a => a.type === 'client');
        if (clients.length > 0) {
          clients.forEach(client => {
            if (!grouped[client.name]) {
              grouped[client.name] = [];
            }
            grouped[client.name].push(task);
          });
        } else {
          // Tasks with no client assignees
          if (!grouped['No Talent Assigned']) {
            grouped['No Talent Assigned'] = [];
          }
          grouped['No Talent Assigned'].push(task);
        }
      }
    });
    
    return grouped;
  };

  /**
   * Toggles the expanded state of a group
   * 
   * @function toggleGroup
   * @param {string} groupName - Name of the group to toggle
   */
  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  /**
   * Expands all groups
   */
  const expandAll = () => {
    const allGroups = Object.keys(getGroupedTasks());
    const newExpandedState = {};
    allGroups.forEach(group => {
      newExpandedState[group] = true;
    });
    setExpandedGroups(newExpandedState);
  };

  /**
   * Collapses all groups
   */
  const collapseAll = () => {
    setExpandedGroups({});
  };

  /**
   * Gets the score for a talent/client based on their name.
   * Looks up the client_id from tasks assigned to this talent and retrieves their score.
   * 
   * @function getTalentScore
   * @param {string} talentName - Name of the talent/client
   * @returns {number|null} The talent's score or null if not found
   */
  const getTalentScore = (talentName) => {
    // Find a task assigned to this talent
    const talentTask = sortedTasks.find(task => {
      const clients = task.assignees?.filter(a => a.type === 'client') || [];
      return clients.some(client => client.name === talentName);
    });

    if (talentTask) {
      const client = talentTask.assignees.find(a => a.type === 'client' && a.name === talentName);
      if (client && client.client_id) {
        const score = clientScores[client.client_id] || null;
        console.log(`Score for ${talentName} (${client.client_id}):`, score);
        return score;
      }
    }
    console.log(`No score found for ${talentName}`);
    return null;
  };

  return (
    <div className="task-manager">
      <div className="task-manager__header">
        <h1>Task Manager</h1>
        <div className="task-manager__header-actions">
          <div className="view-mode-toggle">
            <button 
              className={`view-mode-button ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              Grid View
            </button>
            <button 
              className={`view-mode-button ${viewMode === 'grouped' ? 'active' : ''}`}
              onClick={() => setViewMode('grouped')}
            >
              Grouped View
            </button>
          </div>
          <button 
            className="task-manager__add-button"
            onClick={handleAdd}
          >
            Add Task
          </button>
        </div>
      </div>

      <div className="task-manager__tabs">
        <button 
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => handleTabChange('all')}
        >
          All Tasks
        </button>
        <button 
          className={`tab ${activeTab === 'priority' ? 'active' : ''}`}
          onClick={() => handleTabChange('priority')}
        >
          Priority Tasks
        </button>
        <button 
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => handleTabChange('completed')}
        >
          Completed Tasks
        </button>
      </div>

      <div className="task-manager__search">
        <input
          type="text"
          className="task-search-input"
          placeholder="Search tasks by title, description, assignee, priority, or status..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button 
            className="clear-search-button"
            onClick={() => setSearchQuery('')}
            title="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {/* Grouped View Filter - Show only when in grouped view mode */}
      {viewMode === 'grouped' && (
        <div className="grouped-view-filter">
          <div>
            <button 
              className={`task-grid-filter-btn ${groupedViewFilter === 'regular' ? 'active' : ''}`}
              onClick={() => setGroupedViewFilter('regular')}
            >
              Regular
            </button>
            <button 
              className={`task-grid-filter-btn ${groupedViewFilter === 'talent' ? 'active' : ''}`}
              onClick={() => setGroupedViewFilter('talent')}
            >
              Talent
            </button>
          </div>
          <div className="grouped-filter-expand-controls">
            <button className="task-grid-expand-btn" onClick={expandAll}>Expand All</button>
            <button className="task-grid-expand-btn" onClick={collapseAll}>Collapse All</button>
          </div>
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="task-grid">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th 
                  onClick={() => handleSort('title')} 
                  className="sortable-header" 
                  style={{ width: '20%' }}
                >
                  Title {sortConfig.field === 'title' && (sortConfig.order === 'asc' ? '▲' : '▼')}
                </th>
                <th style={{ width: '28%' }}>Description</th>
                <th 
                  onClick={() => handleSort('priority')} 
                  className="sortable-header" 
                  style={{ width: '8%' }}
                >
                  Priority {sortConfig.field === 'priority' && (sortConfig.order === 'asc' ? '▲' : '▼')}
                </th>
                <th 
                  onClick={() => handleSort('date')} 
                  className="sortable-header" 
                  style={{ width: '10%' }}
                >
                  Date {sortConfig.field === 'date' && (sortConfig.order === 'asc' ? '▲' : '▼')}
                </th>
                <th style={{ width: '12%' }}>Assignees</th>
                <th 
                  onClick={() => handleSort('status')} 
                  className="sortable-header" 
                  style={{ width: '10%' }}
                >
                  Status {sortConfig.field === 'status' && (sortConfig.order === 'asc' ? '▲' : '▼')}
                </th>
                <th style={{ width: '12%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedTasks.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                    {searchQuery ? 'No tasks match your search' : 'No tasks found'}
                  </td>
                </tr>
              ) : (
                sortedTasks.map(task => (
                <tr key={task._id}>
                  <td>
                    <div className="task-title-cell">
                      {task.title}
                      {task.created_by === 'AI Gen' && (
                        <span className="ai-gen-badge-small">AI GEN</span>
                      )}
                    </div>
                  </td>
                  <td className="description-cell">{task.description}</td>
                  <td>
                    <span 
                      className={`priority-dot ${task.priority}`}
                      title={task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                    ></span>
                  </td>
                  <td className="date-cell">{formatDate(task.due_date)}</td>
                  <td>
                    {task.assignees.map(assignee => (
                      <span key={assignee.id} className="assignee-tag">
                        {assignee.name}
                      </span>
                    ))}
                  </td>
                  <td>
                    <div className="status-wrapper">
                      <span className="status-text">{getStatusText(task.status)}</span>
                    </div>
                  </td>
                  <td>
                    <button
                      onClick={() => handleMarkComplete(task)}
                      className="icon-button complete"
                      title="Mark Complete"
                      disabled={task.status === 'complete'}
                    >
                      <CheckIcon />
                    </button>
                    <button
                      onClick={() => handleMarkHold(task)}
                      className="icon-button hold"
                      title="Put On Hold"
                      disabled={task.status === 'hold'}
                    >
                      <PauseIcon />
                    </button>
                    <button 
                      onClick={() => handleEdit(task)} 
                      className="icon-button edit"
                      title="Edit"
                    >
                      <EditIcon />
                    </button>
                    <button 
                      onClick={() => handleDelete(task._id)}
                      className="icon-button delete"
                      title="Delete"
                    >
                      <DeleteIcon />
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        )}
        </div>
      )}

      {/* Grouped View */}
      {viewMode === 'grouped' && (
        <div className="task-grouped-view">
          {loading ? (
            <div>Loading...</div>
          ) : (
            <div className="task-grouped-accordion">
              {Object.entries(getGroupedTasks()).sort((a, b) => a[0].localeCompare(b[0])).map(([groupName, groupTasks]) => {
                // Get the score for this talent/client when in talent view
                const talentScore = groupedViewFilter === 'talent' ? getTalentScore(groupName) : null;
                const hasHighScore = talentScore !== null && talentScore > 65;
                
                if (groupedViewFilter === 'talent') {
                  console.log(`${groupName}: score=${talentScore}, hasHighScore=${hasHighScore}`);
                }
                
                return (
                <div key={groupName} className="task-accordion-item">
                  <div 
                    className="task-accordion-header"
                    onClick={() => toggleGroup(groupName)}
                    style={{
                      backgroundColor: hasHighScore ? 'rgba(144, 238, 144, 0.12)' : undefined
                    }}
                  >
                    <div className="task-accordion-header-left">
                      <span className={`task-accordion-chevron ${expandedGroups[groupName] ? 'expanded' : ''}`}>
                        ▶
                      </span>
                      <h3 className="task-accordion-title">{groupName}</h3>
                      <span className="task-accordion-count">{groupTasks.length}</span>
                    </div>
                  </div>
                  
                  {expandedGroups[groupName] && (
                    <div className="task-accordion-content">
                      <table className="task-accordion-table">
                        <thead>
                          <tr>
                            <th style={{ width: '15%' }}>Title</th>
                            <th style={{ width: '45%' }}>Description</th>
                            <th style={{ width: '8%' }}>Priority</th>
                            <th style={{ width: '8%' }}>Date</th>
                            <th style={{ width: '10%' }}>Status</th>
                            <th style={{ width: '14%' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupTasks.map(task => (
                            <tr key={task._id}>
                              <td>
                                <div className="task-title-cell">
                                  {task.title}
                                  {task.created_by === 'AI Gen' && (
                                    <span className="ai-gen-badge-small">AI GEN</span>
                                  )}
                                </div>
                              </td>
                              <td className="description-cell">{task.description}</td>
                              <td style={{textAlign: 'center'}}>
                                <span 
                                  className={`priority-dot ${task.priority}`}
                                  title={task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                                ></span>
                              </td>
                              <td className="date-cell">{formatDate(task.due_date)}</td>
                              <td style={{textAlign: 'center'}}>
                                <div className="status-wrapper">
                                  <span className="status-text">{getStatusText(task.status)}</span>
                                </div>
                              </td>
                              <td style={{textAlign: 'center'}}>
                                <button
                                  onClick={() => handleMarkComplete(task)}
                                  className="icon-button complete"
                                  title="Mark Complete"
                                  disabled={task.status === 'complete'}
                                >
                                  <CheckIcon />
                                </button>
                                <button
                                  onClick={() => handleMarkHold(task)}
                                  className="icon-button hold"
                                  title="Put On Hold"
                                  disabled={task.status === 'hold'}
                                >
                                  <PauseIcon />
                                </button>
                                <button 
                                  onClick={() => handleEdit(task)} 
                                  className="icon-button edit"
                                  title="Edit"
                                >
                                  <EditIcon />
                                </button>
                                <button 
                                  onClick={() => handleDelete(task._id)}
                                  className="icon-button delete"
                                  title="Delete"
                                >
                                  <DeleteIcon />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                );
              })}
              {Object.keys(getGroupedTasks()).length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  No tasks found for this view
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showTaskModal && (
        <TaskModal
          show={showTaskModal}
          onClose={handleCloseModal}
          task={currentTask}
          onSubmit={handleSubmitTask}
          editMode={editMode}
        />
      )}
    </div>
  );
};

export default TaskGrid; 